import { useEffect } from 'react'

import WebApp from '@twa-dev/sdk'

import { authApiClient, authTokenLocalStorageKey } from '@/lib/api/lib/client'
import { calculateTimeUntilExpiration, getTokenDeadline } from '@/lib/auth'

const MAX_REFRESH_RETRIES = 3
const RETRY_DELAY_MS = 5000

function readToken(): string | null {
    try {
        return localStorage.getItem(authTokenLocalStorageKey)
    } catch {
        // Telegram webviews and Safari private mode can throw on any access
        return null
    }
}

function isStillValid(token: string | null): boolean {
    if (!token) return false
    return calculateTimeUntilExpiration(getTokenDeadline(token)) > 0
}

export function useTokenWorker(): void {
    useEffect(() => {
        let retryCount = 0
        let disposed = false
        const retryTimers: ReturnType<typeof setTimeout>[] = []

        const worker = new Worker(
            new URL('../workers/token.ts', import.meta.url),
            { type: 'module' }
        )

        worker.onmessage = async (event) => {
            const { type, refreshTime } = event.data

            if (type === 'SCHEDULED_REFRESH') {
                console.log(
                    `Token will refresh in ${refreshTime / 1000} seconds`
                )
                retryCount = 0
            } else if (type === 'REFRESH_TOKEN') {
                const refreshingToken = readToken()
                try {
                    const freshToken = await authApiClient.exchange({
                        v1AuthExchangeRequest: {
                            initData: WebApp.initData
                        }
                    })
                    if (!freshToken.token) {
                        throw new Error('Empty token received from exchange')
                    }
                    localStorage.setItem(
                        authTokenLocalStorageKey,
                        freshToken.token
                    )
                    retryCount = 0
                    worker.postMessage({
                        type: 'START_CHECK',
                        token: freshToken.token
                    })
                } catch (error) {
                    console.error('Token refresh failed:', error)
                    retryCount++
                    if (retryCount < MAX_REFRESH_RETRIES) {
                        retryTimers.push(
                            setTimeout(() => {
                                if (disposed) return
                                worker.postMessage({ type: 'RETRY_REFRESH' })
                            }, RETRY_DELAY_MS * retryCount)
                        )
                        return
                    }

                    // Exhausting the retries means the exchange endpoint is
                    // unreachable, not that the session is invalid. Dropping a
                    // token that still works logs the user out over a transient
                    // network failure, so only clear one that already expired.
                    const currentToken = readToken()
                    if (currentToken !== refreshingToken) {
                        // A reauth round trip replaced the token while we were
                        // retrying — the new one is not ours to throw away.
                        retryCount = 0
                        return
                    }
                    if (isStillValid(currentToken)) {
                        retryCount = 0
                        worker.postMessage({
                            type: 'START_CHECK',
                            token: currentToken
                        })
                        return
                    }
                    console.error(
                        'Token refresh exhausted retries, clearing token'
                    )
                    try {
                        localStorage.removeItem(authTokenLocalStorageKey)
                    } catch (storageError) {
                        console.error(
                            'Failed to clear expired token:',
                            storageError
                        )
                    }
                }
            }
        }

        const token = readToken()
        worker.postMessage({ type: 'START_CHECK', token })

        return () => {
            disposed = true
            retryTimers.forEach(clearTimeout)
            retryTimers.length = 0
            worker.terminate()
        }
    }, [])
}
